import React, {useEffect, useState} from 'react';
import {DialogTitle, DialogContent, Stack, Button, Typography, Link, IconButton, Divider, Box} from "@mui/material";
import LoginFields from '../forms/LoginFields';
import {
    Formik,
    Form
} from 'formik';
import logInModel, {logInModelType} from '../forms/models/logInModel';
import logInSchema from '../forms/schemas/logInSchema';
import * as Yup from 'yup';
import {usePostUsersLoginMutation} from '../../redux/vmsApi'
import LoadingCover from '../common/LoadingCover'
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import authSlice from "../../redux/reducers/authSlice";
import { Close, LockOutlined } from '@mui/icons-material';
import useIsMobile from '../../hooks/useIsMobile';
import { styled } from '@mui/material/styles';

const { formField: {logInEmail} } = logInModel;

type MyFormValues = logInModelType

const logInModelInitial = logInModel.initialValues

const LogInFormSchema = Yup.object().shape({...logInSchema})

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: '24px 24px 16px',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
}));

const IconContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
});

const LoginIcon = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.dark,
    borderRadius: '50%',
    padding: '12px',
    marginTop: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: '12px',
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
    },
}));

interface OwnProps {
    close: () => void;
}

const LoginForm = (props: OwnProps) => {
    const {close} = props
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const [updateUserLogin, {isLoading, isSuccess, isError, error}] = usePostUsersLoginMutation()
    const [loginError, setLoginError] = useState('')
    const initialEmail = useAppSelector(state => state.auth.initialEmail)
    const initialValues:MyFormValues = {
        ...logInModelInitial,
        [logInEmail.name]: initialEmail
    };
    const authMessage = useAppSelector(state => state.auth.authMessage);

    const handleSubmit = async (values: MyFormValues) => {
        //here we will handle sending the form through to the api
        const body = {email: values.logInEmail, password: values.logInPass}
        updateUserLogin({body})
    }

    useEffect(() => {
        if(isSuccess) {
            setTimeout(() => {
                close()
            }, 300)
        }
    }, [isSuccess, close])

    useEffect(() => {
        if(isError && error) {
            const err:any = error
            if(err?.data?.error?.message) {
                setLoginError(err?.data?.error?.message)
            }
        }
        else {
            setLoginError('')
        }
    }, [isError, error])

    function openReset() {
        dispatch(authSlice.actions.setAuthState('reset'))
    }

    return (
        <React.Fragment>
            <StyledDialogTitle
                data-cy="dialogue-title" 
                sx={{
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center',
                    justifyContent: isMobile ? 'space-between' : 'center'
                }}
            >
                <Typography variant="h5" component="span" fontWeight="600" color={'white'}>
                    Welcome Back
                </Typography>
                {
                    isMobile ?
                        <IconButton
                            onClick={close}
                            sx={{ color: 'white' }}
                        >
                            <Close
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: 24
                                }}
                            />
                        </IconButton> : null
                }
            </StyledDialogTitle>

            <DialogContent sx={{ padding: '24px', paddingBottom: '32px' }}>
                <IconContainer>
                    <LoginIcon>
                        <LockOutlined sx={{ color: 'white', fontSize: 28 }} />
                    </LoginIcon>
                </IconContainer>
                
                <Typography 
                    align="center" 
                    variant="h6" 
                    fontWeight="500" 
                    sx={{ marginBottom: '16px' }}
                >
                    Sign in to your account
                </Typography>
                
                {authMessage && (
                    <Typography 
                        align="center" 
                        variant="body2" 
                        color="primary" 
                        sx={{ marginBottom: '16px' }}
                    >
                        {authMessage}
                    </Typography>
                )}
                
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={LogInFormSchema}
                >
                    <Form>
                        <Stack spacing={4} width="100%" alignItems="center">
                            <LoginFields apiError={loginError}/>

                            <StyledButton
                                variant="contained"
                                type="submit"
                                fullWidth
                                size="large"
                                data-cy={"loginSubmit"}
                            >
                                Sign In
                            </StyledButton>

                            <Divider sx={{ width: '100%', margin: '8px 0' }} />

                            <Stack spacing={2} width="100%" alignItems="center">
                                <Typography align="center" variant="body2">
                                    <Link 
                                        onClick={openReset}
                                        sx={{ 
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' }
                                        }}
                                    >
                                        Forgot your password?
                                    </Link>
                                </Typography>
                            </Stack>
                        </Stack>
                    </Form>
                </Formik>
            </DialogContent>
            {
                (isLoading || isSuccess) &&
                    <LoadingCover isSuccess={isSuccess} />
            }
        </React.Fragment>
    )
}

export default LoginForm;
